import AddRoundedIcon from "@mui/icons-material/AddRounded";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Alert, Button, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { fetchOssOrderKpiSummary, fetchOssOrders } from "../../api/ossApi";
import { OssExcelUploadDialog } from "../../components/OssExcelUploadDialog";
import { OssOrderFormDialog } from "../../components/OssOrderFormDialog";
import { OssPanel } from "../../components/common/OssPanel";
import { OssPageHeader } from "../../components/common/OssPageHeader";
import { OssOrderSummaryCards } from "../../components/orders/OssOrderSummaryCards";
import { OssOrderInlineDetail } from "../../components/orders/OssOrderInlineDetail";
import { OssOrderTable } from "../../components/orders/OssOrderTable";
import { useOssAsyncData } from "../../hooks/useOssAsyncData";
import { getOrderSummary, getOrderViewData } from "../../utils/orderListUtils";

const initialOrderData = {
  items: [],
  pagination: { page: 1, total: 0, totalPages: 1 },
};

export const OssOrderList = () => {
  const { group_code, project_code } = useParams();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("md"));
  const actionView = searchParams.get("view") === "actions";
  const statusParam = searchParams.get("status") || "";
  const riskLevelParam = searchParams.get("riskLevel") || "";
  const [registerOpen, setRegisterOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(searchParams.get("selectedOrderId") || "");
  const [filters, setFilters] = useState({
    search: "",
    status: actionView ? "CHECK_REQUIRED" : statusParam,
    riskLevel: riskLevelParam,
    sortBy: "requestedDueDate",
    sortDir: "ASC",
    page: 1,
    pageSize: 20,
  });

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      status: actionView ? "CHECK_REQUIRED" : statusParam,
      riskLevel: riskLevelParam,
      page: 1,
    }));
  }, [actionView, statusParam, riskLevelParam]);

  useEffect(() => {
    const nextSelected = searchParams.get("selectedOrderId") || "";
    if (nextSelected) setSelectedOrderId(nextSelected);
  }, [searchParams]);

  const { data, loading, error, load } = useOssAsyncData({
    fetcher: () => fetchOssOrders(group_code, project_code, { ...filters, groupBy: "order" }),
    fallbackData: initialOrderData,
    errorMessage: "주문 목록을 불러오지 못했습니다.",
    deps: [group_code, project_code, filters],
    debounceMs: 250,
  });
  const { data: summary, load: loadSummary } = useOssAsyncData({
    fetcher: () => fetchOssOrderKpiSummary(group_code, project_code),
    fallbackData: { total: 0, checkRequired: 0, changeReview: 0, deliveryRisk: 0 },
    errorMessage: "주문 요약을 불러오지 못했습니다.",
    deps: [group_code, project_code],
  });

  const viewData = useMemo(() => getOrderViewData(data, filters), [data, filters]);
  const fallbackSummary = useMemo(() => getOrderSummary(viewData.items), [viewData.items]);
  const displaySummary = summary.total ? summary : fallbackSummary;
  const updateFilter = (key, value) =>
    setFilters((prev) => {
      const patch = typeof key === "object" ? key : { [key]: value };
      return {
        ...prev,
        ...patch,
        page: Object.prototype.hasOwnProperty.call(patch, "page") ? patch.page : 1,
      };
    });

  const selectOrder = (orderId) => {
    setSelectedOrderId(String(orderId || ""));
  };

  return (
    <Stack className="oss-page oss-page--orders" spacing={2}>
      <OssPageHeader
        title={actionView ? "확인 필요 주문" : "주문 목록"}
        iconName="checklistIcon"
        actions={
          <>
          <Button variant="outlined" startIcon={<FileUploadOutlinedIcon />} onClick={() => setUploadOpen(true)}>
            Excel 업로드
          </Button>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setRegisterOpen(true)}>
            주문 등록
          </Button>
          </>
        }
      />

      {error && <Alert severity="error">{error}</Alert>}

      <OssOrderSummaryCards summary={displaySummary} filters={filters} onFilterChange={updateFilter} />

      <OssPanel className="oss-list-panel" sx={{ p: 1.25, overflow: "hidden", bgcolor: "#fff" }}>
        <OssOrderTable
          compact={compact}
          filters={filters}
          loading={loading}
          viewData={viewData}
          onFilterChange={updateFilter}
          selectedOrderId={selectedOrderId}
          onOpen={selectOrder}
        />
      </OssPanel>

      <OssOrderInlineDetail
        open={Boolean(selectedOrderId)}
        groupCode={group_code}
        projectCode={project_code}
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId("")}
        onChanged={() => {
          load();
          loadSummary();
        }}
      />

      <OssOrderFormDialog
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        groupCode={group_code}
        projectCode={project_code}
        onCreated={() => {
          load();
          loadSummary();
        }}
      />
      <OssExcelUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        groupCode={group_code}
        projectCode={project_code}
        onImported={() => {
          load();
          loadSummary();
        }}
      />
    </Stack>
  );
};
